import { Component, Input, OnDestroy } from '@angular/core';
import { UntypedFormGroup, Validators, FormBuilder } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NotifierService } from 'angular-notifier';
import { Subject, takeUntil } from 'rxjs';
import { SoftPurgeConfig } from './soft-purge-confirmation-modal.model';

/**
 * Reusable Soft Purge Confirmation Modal Component
 *
 * This component provides a consistent, safety-focused confirmation dialog
 * for irreversible data purge operations across the application.
 *
 * Features:
 * - Configurable purge items list
 * - Bank account details display
 * - Text-based confirmation with account ID
 * - Loading state during API calls
 * - Success/error notification handling
 *
 * Usage:
 * ```typescript
 * const modalRef = this.modalService.open(SoftPurgeConfirmationModalComponent, {
 *   size: 'md',
 *   backdrop: 'static',
 *   keyboard: false,
 *   centered: true
 * });
 * modalRef.componentInstance.config = {
 *   title: 'Soft Purge Historical Data',
 *   purgeItems: ['Bank Transactions', 'Payment History'],
 *   bankAccountDetails: { ... },
 *   confirmationIdentifier: bankAccount.id,
 *   onConfirm: () => this.service.softPurge(id),
 *   successMessage: 'Data purged successfully'
 * };
 * ```
 */
@Component({
    selector: 'app-soft-purge-confirmation-modal',
    templateUrl: './soft-purge-confirmation-modal.component.html',
    styleUrls: ['./soft-purge-confirmation-modal.component.scss'],
    standalone: false
})
export class SoftPurgeConfirmationModalComponent implements OnDestroy {

    @Input() config!: SoftPurgeConfig;

    private _unsubscribeAll: Subject<void> = new Subject<void>();

    confirmationForm: UntypedFormGroup;
    isProcessing: boolean = false;

    constructor(
        public activeModal: NgbActiveModal,
        private _formBuilder: FormBuilder,
        private _notifierService: NotifierService
    ) {
        this.confirmationForm = this._formBuilder.group({
            confirmationInput: [null, [Validators.required]]
        });
    }

    /**
     * Get the expected confirmation text based on the confirmation identifier
     */
    get expectedConfirmationText(): string {
        return this.config?.confirmationText || `I Want To Purge Data For ${this.config?.confirmationIdentifier || ''}`;
    }

    /**
     * Check if the confirmation input matches the expected text
     */
    get isConfirmationValid(): boolean {
        return this.confirmationForm.get('confirmationInput')?.value === this.expectedConfirmationText;
    }

    /**
     * Check if the input has been touched and doesn't match
     */
    get showValidationError(): boolean {
        const input = this.confirmationForm.get('confirmationInput');
        return input?.touched && input?.value !== this.expectedConfirmationText;
    }

    /**
     * Handle the confirmation action
     * Validates input and executes the configured onConfirm callback
     */
    onConfirm(): void {
        if (!this.isConfirmationValid) {
            return;
        }

        this.isProcessing = true;

        this.config.onConfirm()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this._notifierService.notify('success', this.config.successMessage);
                    this.activeModal.close('confirmed');
                    this.isProcessing = false;
                },
                error: () => {
                    this.isProcessing = false;
                }
            });
    }

    /**
     * Dismiss the modal
     */
    onCancel(): void {
        this.activeModal.dismiss('cancel');
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
